import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Car, Sparkles, ArrowUpRight } from "lucide-react";
import { MANIFESTO } from "@/data/site";

const ICONS = { clock: Clock, car: Car, sparkles: Sparkles };

export default function Manifesto() {
  const [active, setActive] = useState(0);

  return (
    <section id="manifesto" data-testid="manifesto" className="bg-[#F8F9FA] py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="max-w-2xl mb-16 md:mb-20">
          <span className="text-xs tracking-[0.35em] uppercase text-[#1C2541]/50">L'approche OKA</span>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight mt-5 text-[#0B132B]">
            Bien plus qu'un trajet, <span className="italic text-[#3a4a72]">une expérience</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Image de gauche, synchronisée avec le point actif (desktop uniquement) */}
          <div className="hidden lg:block lg:col-span-5">
            <div className="sticky top-28">
              <div className="relative aspect-[4/5] overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={active}
                    src={MANIFESTO[active].image}
                    alt={MANIFESTO[active].title}
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.02 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={active}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.4 }}
                      className="font-display text-white text-6xl leading-none"
                    >
                      {MANIFESTO[active].n}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>

          {/* Liste interactive de droite */}
          <div className="lg:col-span-7">
            {MANIFESTO.map((m, i) => {
              const Icon = ICONS[m.icon] ?? Sparkles;
              const isActive = active === i;
              return (
                <motion.div
                  key={m.n}
                  data-testid={`manifesto-item-${m.n}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  onViewportEnter={() => setActive(i)}
                  onMouseEnter={() => setActive(i)}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className={`group relative grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-start py-10 px-4 md:px-6 -mx-4 md:-mx-6 border-t border-[#0B132B]/10 cursor-default transition-colors duration-500 ${
                    isActive ? "bg-white" : "bg-transparent"
                  }`}
                >
                  <div className="md:col-span-2 flex md:block items-center gap-4">
                    <span
                      className={`font-display text-6xl md:text-7xl leading-none transition-colors duration-500 ${
                        isActive ? "text-[#0B132B]/20" : "text-[#0B132B]/10"
                      }`}
                    >
                      {m.n}
                    </span>
                  </div>

                  <div className="md:col-span-1 flex justify-start pt-2">
                    <span
                      className={`w-11 h-11 flex items-center justify-center border transition-all duration-500 ${
                        isActive ? "bg-[#0B132B] border-[#0B132B] text-white" : "border-[#0B132B]/15 text-[#0B132B]/50"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </span>
                  </div>

                  <h3 className="md:col-span-5 font-display text-2xl md:text-3xl text-[#0B132B] leading-tight pt-1">
                    {m.title}
                  </h3>
                  <p className="md:col-span-3 text-[#1C2541]/70 leading-relaxed pt-1">{m.body}</p>

                  <ArrowUpRight
                    className={`hidden md:block absolute right-4 top-10 w-5 h-5 text-[#0B132B] transition-all duration-500 ${
                      isActive ? "opacity-100 translate-x-0 translate-y-0" : "opacity-0 -translate-x-1 translate-y-1"
                    }`}
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
